import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api'

export interface Medication {
  id: number
  name: string
  dosage?: string | null
  frequency?: string | null
  as_needed?: boolean
  notes?: string | null
  active?: boolean
  created_at?: string | null
  // Optional anchor for cyclic hormonal therapy (pill-start date). Drives the
  // withdrawal-bleed estimate in the cycle predictor; falls back to last bleed.
  regimen_start_date?: string | null
}

interface MedicationsResponse {
  medications: Medication[]
  no_medications_declared?: boolean
}

export interface MedicationInput {
  name: string
  dosage?: string | null
  frequency?: string | null
  as_needed?: boolean
  notes?: string | null
  regimen_start_date?: string | null
}

export function useMedications(activeOnly = true) {
  return useQuery({
    queryKey: ['patient', 'medications', activeOnly],
    queryFn: () =>
      api.get<MedicationsResponse>(`/api/patient/medications?active_only=${activeOnly}`),
    staleTime: 60_000,
  })
}

export function useAddMedication() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: MedicationInput) =>
      api.post<{ success: boolean; id: number }>('/api/patient/medications', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient', 'medications'] })
      qc.invalidateQueries({ queryKey: ['patient', 'calendar'] })
      // Refreshes the setup checklist (medications item) on the dashboard.
      qc.invalidateQueries({ queryKey: ['patient', 'dashboard'] })
    },
  })
}

/**
 * Declare (or undo) that the user takes no medications. Completes the setup
 * checklist's medications item for users with nothing to add.
 */
export function useDeclareNoMedications() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (declared: boolean) =>
      api.post<{ success: boolean; no_medications_declared: boolean }>(
        '/api/patient/medications/none',
        { declared },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient', 'medications'] })
      qc.invalidateQueries({ queryKey: ['patient', 'dashboard'] })
    },
  })
}

export function useUpdateMedication() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: MedicationInput }) =>
      api.put(`/api/patient/medications/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient', 'medications'] })
      qc.invalidateQueries({ queryKey: ['patient', 'calendar'] })
    },
  })
}

export function useDeleteMedication() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.del(`/api/patient/medications/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient', 'medications'] })
      qc.invalidateQueries({ queryKey: ['patient', 'calendar'] })
      qc.invalidateQueries({ queryKey: ['patient', 'dashboard'] })
    },
  })
}
